import { apiBase, env } from "@/env";
import type { ApiError } from "@/lib/api/types";
import {
  clearSession,
  getSession,
  needsRefresh,
  setSession,
  type StoredSession,
} from "@/lib/session/tokens";

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fields: Record<string, string>;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

export type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;

  binary?: { body: Blob; contentType: string };

  upload?: boolean;
  query?: Record<string, string | number | boolean | undefined>;

  anonymous?: boolean;
  signal?: AbortSignal;

  onProgress?: (fraction: number) => void;
};

function url(path: string, query: RequestOptions["query"], upload = false): string {
  const base = upload ? `${env.apiOrigin}/upload` : apiBase();
  const target = `${base}/${path.replace(/^\/+/u, "")}`;
  if (query === undefined) return target;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered === "" ? target : `${target}?${rendered}`;
}

let refreshInFlight: Promise<StoredSession | null> | null = null;

async function refreshTokens(): Promise<StoredSession | null> {
  const session = getSession();
  if (session === null) return null;

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(url("auth/refresh", undefined), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      });
      if (!response.ok) {
        clearSession();
        return null;
      }
      const result = (await response.json()) as {
        accessToken: string;
        refreshToken: string;
        expiresIn: number;
        user: StoredSession["user"];
      };
      return setSession(result, result.user);
    } catch {
      return session;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function socketAccessToken(force = false): Promise<string | null> {
  let session = getSession();
  if (session === null) return null;
  if (force || needsRefresh(session)) session = await refreshTokens();
  return session?.accessToken ?? null;
}

async function authorization(anonymous: boolean): Promise<string | null> {
  if (anonymous) return null;
  let session = getSession();
  if (session === null) return null;
  if (needsRefresh(session)) session = (await refreshTokens()) ?? session;
  return `Bearer ${session.accessToken}`;
}

async function toError(response: Response): Promise<ApiRequestError> {
  let code = "http_error";
  let message = `Request failed with status ${response.status}`;
  let fields: Record<string, string> = {};
  try {
    const body = (await response.json()) as ApiError;
    if (typeof body.error?.code === "string") code = body.error.code;
    if (typeof body.error?.message === "string") message = body.error.message;
    if (body.error?.fields !== undefined) fields = body.error.fields;
  } catch (error) {
    console.error("error body parse failed:", error);
  }
  return new ApiRequestError(response.status, code, message, fields);
}

async function send<T>(path: string, options: RequestOptions, retry: boolean): Promise<T> {
  const headers = new Headers();
  const auth = await authorization(options.anonymous === true);
  if (auth !== null) headers.set("Authorization", auth);

  let body: BodyInit | undefined;
  if (options.binary !== undefined) {
    headers.set("Content-Type", options.binary.contentType);
    body = options.binary.body;
  } else if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.body);
  }

  const init: RequestInit = { method: options.method ?? "GET", headers };
  if (body !== undefined) init.body = body;
  if (options.signal !== undefined) init.signal = options.signal;

  const response = await fetch(url(path, options.query, options.upload === true), init);

  if (response.status === 401 && retry && options.anonymous !== true) {
    const refreshed = await refreshTokens();
    if (refreshed !== null) return send<T>(path, options, false);
    clearSession();
  }

  if (!response.ok) throw await toError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function sendBinary<T>(path: string, options: RequestOptions): Promise<T> {
  const auth = await authorization(options.anonymous === true);
  const payload = options.binary;
  if (payload === undefined) throw new Error("sendBinary called without a body");
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(options.method ?? "POST", url(path, options.query, options.upload === true));
    if (auth !== null) request.setRequestHeader("Authorization", auth);
    request.setRequestHeader("Content-Type", payload.contentType);

    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && options.onProgress !== undefined) {
        options.onProgress(event.loaded / event.total);
      }
    });
    request.addEventListener("load", () => {
      if (request.status >= 200 && request.status < 300) {
        resolve(
          request.responseText === "" ? (undefined as T) : (JSON.parse(request.responseText) as T),
        );
        return;
      }
      let code = "http_error";
      let message = `Upload failed with status ${request.status}`;
      try {
        const parsed = JSON.parse(request.responseText) as ApiError;
        code = parsed.error?.code ?? code;
        message = parsed.error?.message ?? message;
      } catch (error) {
        console.error("upload error body parse failed:", error);
      }
      reject(new ApiRequestError(request.status, code, message));
    });
    request.addEventListener("error", () => {
      reject(new ApiRequestError(0, "network_error", "The upload could not reach the server"));
    });
    request.addEventListener("abort", () => {
      reject(new ApiRequestError(0, "aborted", "The upload was cancelled"));
    });
    options.signal?.addEventListener("abort", () => {
      request.abort();
    });
    request.send(payload.body);
  });
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (options.binary !== undefined) return sendBinary<T>(path, options);
  return send<T>(path, options, true);
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong";
}
